import { PasswordChecklist, meetsPolicy } from '@/components/PasswordChecklist';
import { Spinner } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

/**
 * Alta de una cuenta.
 *
 * Son dos pasos a proposito: primero se comprueban la invitacion y la cuenta de
 * Google, y solo despues se pide el resto. Asi nadie rellena un formulario
 * entero para descubrir al final que su token ya se habia usado.
 *
 * Google solo demuestra que el correo es suyo; quien autoriza la entrada es la
 * invitacion, y la sesion sigue siendo con contrasena.
 */
export function RegisterPage() {
  const [googleEnabled, setGoogleEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    api
      .get<{ googleEnabled: boolean }>('/auth/registration-config')
      .then((config) => setGoogleEnabled(config.googleEnabled))
      .catch(() => setGoogleEnabled(false));
  }, []);

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-6 py-12">
      <div className="w-full max-w-[440px]">
        <div className="mb-7 flex items-center gap-2.5">
          <div
            aria-hidden="true"
            className="flex size-[30px] items-center justify-center rounded-[9px] font-display text-[15px] font-bold text-white"
            style={{ background: 'linear-gradient(135deg,#14b8a6,#22d3ee)' }}
          >
            S
          </div>
          <span className="font-display text-base font-bold tracking-[-.01em]">SynQ</span>
        </div>

        {googleEnabled === null && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}

        {googleEnabled === false && (
          <div className="rounded-panel border border-warn-line bg-warn-soft p-5 text-[13.5px] text-warn-text text-pretty">
            <p className="font-semibold">El registro con Google no esta configurado.</p>
            <p className="mt-1.5">
              Falta la credencial de Google en el servidor. Avisa al administrador para que la
              agregue, o pidele que te cree la cuenta a mano.
            </p>
          </div>
        )}

        {googleEnabled && GOOGLE_CLIENT_ID && (
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <RegisterFlow />
          </GoogleOAuthProvider>
        )}

        {googleEnabled && !GOOGLE_CLIENT_ID && (
          <div className="rounded-panel border border-warn-line bg-warn-soft p-5 text-[13.5px] text-warn-text text-pretty">
            Falta <span className="font-mono">VITE_GOOGLE_CLIENT_ID</span> en esta aplicacion.
          </div>
        )}

        <p className="mt-6 text-center text-[12.5px] text-subtle">
          Ya tienes cuenta?{' '}
          <Link to="/login" className="font-semibold text-brand-deep hover:underline">
            Inicia sesion
          </Link>
        </p>
      </div>
    </main>
  );
}

function RegisterFlow() {
  const navigate = useNavigate();

  const [inviteToken, setInviteToken] = useState('');
  const [googleIdToken, setGoogleIdToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function handleGoogle(credential: string) {
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<{ email: string }>('/auth/register/start', {
        googleIdToken: credential,
        inviteToken: inviteToken.trim(),
      });
      setGoogleIdToken(credential);
      setEmail(result.email);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(0, 'No se pudo verificar'));
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/auth/register', {
        googleIdToken,
        inviteToken: inviteToken.trim(),
        fullName: fullName.trim(),
        jobTitle: jobTitle.trim() || undefined,
        password,
      });
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(0, 'No se pudo crear la cuenta'));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-panel border border-success-line bg-success-soft p-6 text-center">
        <h1 className="font-display text-lg font-semibold text-success-text">Cuenta creada</h1>
        <p className="mt-2 text-[13.5px] text-body-soft text-pretty">
          Ya puedes entrar a SynQ con <span className="font-mono">{email}</span> y la contrasena
          que acabas de elegir.
        </p>
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          className="mt-5 w-full rounded-control bg-brand px-3 py-2.5 text-[13.5px] font-semibold text-brand-on transition-colors hover:bg-brand-strong"
        >
          Iniciar sesion
        </button>
      </div>
    );
  }

  const mismatch = confirmation.length > 0 && confirmation !== password;
  const canSubmit =
    fullName.trim().length >= 2 && meetsPolicy(password) && !mismatch && confirmation.length > 0;

  return (
    <div className="rounded-panel border border-line bg-surface p-6">
      <h1 className="font-display text-[20px] font-semibold tracking-[-.015em]">Crea tu cuenta</h1>
      <p className="mt-1.5 text-[13.5px] leading-relaxed text-subtle text-pretty">
        Necesitas el token de registro que te envio el administrador.
      </p>

      {/* Paso 1: el token y la verificacion con Google. */}
      <div className="mt-6">
        <label htmlFor="invite" className="mb-1.5 block text-[12.5px] font-semibold">
          Token de registro
        </label>
        <input
          id="invite"
          value={inviteToken}
          onChange={(e) => setInviteToken(e.target.value)}
          disabled={Boolean(email)}
          placeholder="Pega aqui el token que te enviaron"
          className="w-full rounded-control border border-line-strong bg-surface px-3 py-2.5 font-mono text-[13px] outline-none transition-colors focus:border-brand disabled:opacity-60"
        />
      </div>

      {!email && (
        <div className="mt-5">
          <p className="mb-2 text-[12.5px] font-semibold">Verifica tu correo con Google</p>
          {inviteToken.trim().length === 0 ? (
            <p className="rounded-control border border-line-strong bg-sunken px-3 py-2.5 text-[12.5px] text-faint">
              Escribe primero el token de registro.
            </p>
          ) : busy ? (
            <div className="flex justify-center py-3">
              <Spinner />
            </div>
          ) : (
            <GoogleLogin
              onSuccess={(response) => {
                if (response.credential) {
                  void handleGoogle(response.credential);
                }
              }}
              onError={() => setError(new ApiError(0, 'Google cancelo el inicio de sesion'))}
              theme="filled_black"
              shape="rectangular"
              text="signup_with"
              width="376"
            />
          )}
        </div>
      )}

      {/* Paso 2: el resto de los datos, ya con el correo confirmado. */}
      {email && (
        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4" noValidate>
          <div className="rounded-control border border-success-line bg-success-soft px-3 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-[.1em] text-success-text">
              Correo verificado
            </p>
            <p className="mt-0.5 font-mono text-[13px]">{email}</p>
          </div>

          <div>
            <label htmlFor="name" className="mb-1.5 block text-[12.5px] font-semibold">
              Nombre completo
            </label>
            <input
              id="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoFocus
              required
              className="w-full rounded-control border border-line-strong bg-surface px-3 py-2.5 text-[13.5px] outline-none focus:border-brand"
            />
          </div>

          <div>
            <label htmlFor="job" className="mb-1.5 block text-[12.5px] font-semibold">
              Rol <span className="font-normal text-faint">(tu puesto, en tus palabras)</span>
            </label>
            <input
              id="job"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Desarrolladora backend, QA, Diseno…"
              className="w-full rounded-control border border-line-strong bg-surface px-3 py-2.5 text-[13.5px] outline-none focus:border-brand"
            />
          </div>

          <div>
            <label htmlFor="pass" className="mb-1.5 block text-[12.5px] font-semibold">
              Contrasena
            </label>
            <input
              id="pass"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-control border border-line-strong bg-surface px-3 py-2.5 text-[13.5px] outline-none focus:border-brand"
            />
            <div className="mt-2.5 rounded-control bg-sunken p-3">
              <PasswordChecklist password={password} />
            </div>
          </div>

          <div>
            <label htmlFor="confirm" className="mb-1.5 block text-[12.5px] font-semibold">
              Confirma la contrasena
            </label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              aria-invalid={mismatch}
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              className={`w-full rounded-control border bg-surface px-3 py-2.5 text-[13.5px] outline-none ${
                mismatch ? 'border-danger' : 'border-line-strong focus:border-brand'
              }`}
            />
            {mismatch && (
              <p className="mt-1.5 text-[11.5px] text-danger-text">Las contrasenas no coinciden</p>
            )}
          </div>

          <button
            type="submit"
            disabled={!canSubmit || busy}
            className="mt-1 w-full rounded-control bg-brand px-3 py-2.5 text-[13.5px] font-semibold text-brand-on transition-colors hover:bg-brand-strong disabled:opacity-50"
          >
            {busy ? 'Creando…' : 'Crear cuenta'}
          </button>
        </form>
      )}

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-control border border-danger-line bg-danger-soft px-3 py-2 text-[13px] text-danger-text"
        >
          <p>{error.message}</p>
          {error.errors && error.errors.length > 0 && (
            <ul className="mt-1 list-inside list-disc text-[11.5px]">
              {error.errors.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
